import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Minimize2, Monitor, Tablet, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import { ChangelogPreviewIframe } from "./changelog-preview-iframe";
import type { ChangelogPreviewData } from "./changelog-preview-html-builder";

type DeviceWidth = "desktop" | "tablet" | "mobile";

const DEVICE_WIDTHS: Record<DeviceWidth, number | null> = {
  desktop: null,
  tablet: 768,
  mobile: 375,
};

interface ChangelogFullscreenPreviewProps {
  previewData: ChangelogPreviewData;
  onClose: () => void;
}

export function ChangelogFullscreenPreview({
  previewData,
  onClose,
}: ChangelogFullscreenPreviewProps) {
  const [device, setDevice] = useState<DeviceWidth>("desktop");
  const maxWidth = DEVICE_WIDTHS[device];

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    },
    [onClose],
  );

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [handleKeyDown]);

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex flex-col bg-white"
      role="dialog"
      aria-modal="true"
      aria-label="전체화면 미리보기"
    >
      {/* Toolbar */}
      <div className="flex items-center justify-between border-b bg-white px-4 py-2">
        <span className="text-sm font-medium">전체화면 미리보기</span>

        <div className="flex items-center gap-3">
          {/* Device width selector */}
          <TooltipProvider>
            <div className="flex items-center gap-1 rounded-md border p-0.5">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={device === "desktop" ? "secondary" : "ghost"}
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => setDevice("desktop")}
                    aria-label="데스크탑 보기"
                  >
                    <Monitor className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Desktop</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={device === "tablet" ? "secondary" : "ghost"}
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => setDevice("tablet")}
                    aria-label="태블릿 보기"
                  >
                    <Tablet className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Tablet (768px)</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={device === "mobile" ? "secondary" : "ghost"}
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => setDevice("mobile")}
                    aria-label="모바일 보기"
                  >
                    <Smartphone className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Mobile (375px)</TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>

          {/* Close button */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={onClose}
                  aria-label="전체화면 닫기"
                >
                  <Minimize2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>닫기 (Esc)</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Preview area */}
      <div className="flex flex-1 items-start justify-center overflow-hidden bg-gray-50">
        <div
          className="h-full"
          style={{
            width: maxWidth ? `${maxWidth}px` : "100%",
            maxWidth: maxWidth ? `${maxWidth}px` : undefined,
            transition: "width 200ms ease",
          }}
        >
          <ChangelogPreviewIframe previewData={previewData} />
        </div>
      </div>
    </div>,
    document.body,
  );
}
